import { useCallback, useRef, useState, useEffect } from 'react';
import { Button, CenterScreenModal } from '../atoms';
import { NormalFormTextArea } from '../molecules';
import {
  useErrorHandlerContext,
  useIncidentDetailContext,
  useUpdateStatusWithNoteModalContext,
  useUserLanguage,
} from '@/customHooks';
import { useCreateNoteFromIncidentMutation } from '@/graphql/generated';
import textsData from '@/textData/IncidentDetailTexts.json';

const TEXT_LIMIT = 2000;

export const UpdateStatusWithNoteModal = () => {
  const userLanguage = useUserLanguage();
  const texts = userLanguage === 'es' ? textsData.es : textsData.en;

  const { isOpen, initialNote, onConfirm, closeModal } = useUpdateStatusWithNoteModalContext();
  const { incident } = useIncidentDetailContext();
  const { open: errorHandle } = useErrorHandlerContext();

  const [createNote, { loading: noteLoading }] = useCreateNoteFromIncidentMutation();
  const [availableChars, setAvailableChars] = useState(TEXT_LIMIT);
  const textRef = useRef<string>(initialNote || '');

  useEffect(() => {
    if (isOpen) {
      textRef.current = initialNote || '';
      setAvailableChars(TEXT_LIMIT - (initialNote?.length || 0));
    }
  }, [isOpen, initialNote]);

  const onChangeHandle = useCallback((text: string | null) => {
    const safeText = text || '';
    if (safeText.length > TEXT_LIMIT) return;
    textRef.current = safeText;
    setAvailableChars(TEXT_LIMIT - safeText.length);
  }, []);

  const onConfirmHandle = async () => {
    if (!incident) return;

    try {
      if (textRef.current.trim()) {
        await createNote({
          variables: {
            incidentId: incident.id,
            description: textRef.current,
          },
        });
      }
      
      if (onConfirm) {
        onConfirm();
      }
      
      closeModal();
    } catch (error) {
      errorHandle(error);
    }
  };

  const charsHint = userLanguage === 'es'
    ? `${availableChars} disponibles`
    : `${availableChars} characters left`;

  return (
    <CenterScreenModal isOpen={isOpen} onClose={closeModal}>
      <div className="flex flex-col">
        <div className="w-full">
          <h2 className="mb-4 text-xl font-semibold text-gray-900">
            {userLanguage === 'es' ? 'Agregar Nota de Seguimiento' : 'Add Tracking Note'}
          </h2>
        </div>
        
        <div className="mb-6">
          <NormalFormTextArea
            placeholder={texts.notes.form.placeHolder}
            label={texts.notes.form.label}
            onChange={onChangeHandle}
            value={initialNote}
          />
          <div className="mt-2 flex justify-between">
            <p className="text-sm text-gray-500 italic">
              {charsHint}
            </p>
          </div>
        </div>

        <div className="flex flex-row justify-center items-center gap-8 mt-4">
          <div className="flex-none">
            <Button secondary onClick={closeModal} disabled={noteLoading}>
              {userLanguage === 'es' ? 'Cancelar' : 'Volver'}
            </Button>
          </div>
          <div className="flex-none">
            <Button
              onClick={onConfirmHandle}
              isLoading={noteLoading}
              disabled={availableChars < 0}
            >
              {userLanguage === 'es' ? 'Aceptar' : 'Accept'}
            </Button>
          </div>
        </div>
      </div>
    </CenterScreenModal>
  );
};
